import React, { createElement } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import className from "./style.module.scss";
import {
  sanitizeUnsafeHTML,
  getCustomSanitizeOptions,
  sanitizeConfigOptions,
} from "@labxchange/util-sanitization";

// This component addresses HTML SANITIZATION and FORMATTING for strings
// It also assists in managing TRANSLATIONS with react-intl formatMessage
// Custom tags can be established, imported and supported here
// Defined formatting categories
// When adding new categories, be sure to add it to the "oneOf" list in the PropTypes
export function FormattedMessage(props) {
  const intl = useIntl();
  const { allowedTags } = getCustomSanitizeOptions(
    props.sanitizeConfig,
    props.allowAdditionalTags,
    props.excludeTags
  );

  const decodeChunks = (chunks) => {
    // React.Children.map handles arrays, strings, and elements uniformly.
    return React.Children.map(chunks, (child) => {
      if (typeof child === "string") {
        // decode any HTML entities (including "&nbsp;") to real characters
        return decodeHtmlEntities(child);
      }
      if (React.isValidElement(child) && child.props.children) {
        // If it's a React element, clone it and decode its children too
        return React.cloneElement(
          child,
          { ...child.props },
          decodeChunks(child.props.children)
        );
      }
      // If it's something else (e.g. a number, null, etc.), just return as-is
      return child;
    });
  };

  // Extract plain text from React content (string, element, or array) for aria-label
  // This ensures screen readers announce the content correctly without DOM fragmentation issues
  const extractPlainText = (content) => {
    const tempDiv = document.createElement("div");

    const renderToString = (item) => {
      if (typeof item === "string") {
        return item;
      }
      if (React.isValidElement(item)) {
        return renderToString(item.props.children);
      }
      if (Array.isArray(item)) {
        return item.map(renderToString).join("");
      }
      return "";
    };

    const htmlString = renderToString(content);
    tempDiv.innerHTML = htmlString;
    const plainText = tempDiv.innerText;

    return plainText;
  };

  const formatBasicTag = (tag, chunks) => {
    if (allowedTags.includes(tag)) {
      return createElement(
        tag,
        {
          dir: "auto",
          className: className[tag],
        },
        decodeChunks(chunks)
      );
    } else return decodeChunks(chunks);
  };

  const formatTagWithAttributes = (() => {
    // This uses the attributes from the key of the strings, so any changes in translations will be ignored
    const tagCounters = {}; // Track how many times each tag has been used to ensure correct occurance and avoid conflicts
    return (tag, chunks, attributeArray) => {
      if (!allowedTags.includes(tag)) return chunks;
      // Initialize counter for this tag
      if (!tagCounters[tag]) {
        tagCounters[tag] = 0;
      }
      const attributes = getAttributes(tag, attributeArray);
      const index = tagCounters[tag]; // Use current index for this tag
      tagCounters[tag]++; // Increment for next occurrence
      if (!attributes[tag] || !attributes[tag][index]) {
        return chunks;
      }
      const tagAttributes = attributes[tag][index];
      switch (tag) {
        case "a":
          return (
            <a
              className={className.internalLink}
              href={tagAttributes.url || null}
              target="_blank"
              rel="noopener noreferrer"
              dir="auto"
            >
              {decodeChunks(chunks)}
              <span className={className["sr-only"]}>
                {" "}
                (
                {intl.formatMessage({
                  id: "Open link in a new tab",
                  defaultMessage: "Open link in a new tab",
                })}
                )
              </span>
            </a>
          );
        case "abbr":
          return (
            <abbr
              className={className.abbr}
              title={tagAttributes.title || null}
              dir="auto"
            >
              {decodeChunks(chunks)}
            </abbr>
          );
        case "highlight":
          return (
            <span
              className={className.highlight}
              dir="auto"
              style={{ backgroundColor: tagAttributes.color || null }}
            >
              {decodeChunks(chunks)}
            </span>
          );
        default:
          return decodeChunks(chunks);
      }
    };
  })();

  const getAttributes = (tag, attributeKeys) => {
    const attributes = {};
    const regex = new RegExp(`<${tag}[^>]*?>(.*?)<\\/${tag}>`, "g");
    const matches = props.string.match(regex);
    if (matches) {
      attributes[tag] = [];
      matches.forEach((match) => {
        const attrObj = {
          content: match.replace(
            new RegExp(`<${tag}[^>]*?>|<\\/${tag}>`, "g"),
            ""
          ),
        };
        attributeKeys.forEach((key) => {
          const keyRegex = new RegExp(`${key}=['"]([^'"]*)['"]`, "g");
          let keyMatch;
          while ((keyMatch = keyRegex.exec(match)) !== null) {
            attrObj[key] = keyMatch[1];
          }
        });
        attributes[tag].push(attrObj);
      });
    }
    return attributes;
  };

  // Once the string sanitised, we need to decaode any remaining / safe HTML entities natively
  const decodeHtmlEntities = (encodedString) => {
    const textArea = document.createElement("textarea");
    textArea.innerHTML = encodedString;
    return textArea.value;
  };

  const formattedMessage = intl.formatMessage(
    {
      id: props.string,
      defaultMessage: decodeHtmlEntities(
        sanitizeUnsafeHTML(props.fallbackString || props.string, {
          allowedTags,
        })
      ),
      description: "",
    },
    {
      // All supported tags are listed below
      // further filtering is applied in the "formatTag" function, by sanitizeConfig
      p: (chunks) => formatBasicTag("p", decodeChunks(chunks)),
      h1: (chunks) => formatBasicTag("h1", decodeChunks(chunks)),
      h2: (chunks) => formatBasicTag("h2", decodeChunks(chunks)),
      h3: (chunks) => formatBasicTag("h3", decodeChunks(chunks)),
      h4: (chunks) => formatBasicTag("h4", decodeChunks(chunks)),
      h5: (chunks) => formatBasicTag("h5", decodeChunks(chunks)),
      h6: (chunks) => formatBasicTag("h6", decodeChunks(chunks)),
      b: (chunks) => formatBasicTag("b", decodeChunks(chunks)),
      strong: (chunks) => formatBasicTag("strong", decodeChunks(chunks)),
      i: (chunks) => formatBasicTag("i", decodeChunks(chunks)),
      em: (chunks) => formatBasicTag("em", decodeChunks(chunks)),
      sup: (chunks) => formatBasicTag("sup", decodeChunks(chunks)),
      sub: (chunks) => formatBasicTag("sub", decodeChunks(chunks)),
      s: (chunks) => formatBasicTag("s", decodeChunks(chunks)),
      u: (chunks) => formatBasicTag("u", decodeChunks(chunks)),
      ul: (chunks) => formatBasicTag("ul", decodeChunks(chunks)),
      ol: (chunks) => formatBasicTag("ol", decodeChunks(chunks)),
      li: (chunks) => formatBasicTag("li", decodeChunks(chunks)),
      dl: (chunks) => formatBasicTag("dl", decodeChunks(chunks)),
      dt: (chunks) => formatBasicTag("dt", decodeChunks(chunks)),
      dd: (chunks) => formatBasicTag("dd", decodeChunks(chunks)),
      table: (chunks) => formatBasicTag("table", decodeChunks(chunks)),
      thead: (chunks) => formatBasicTag("thead", decodeChunks(chunks)),
      tbody: (chunks) => formatBasicTag("tbody", decodeChunks(chunks)),
      tr: (chunks) => formatBasicTag("tr", decodeChunks(chunks)),
      th: (chunks) => formatBasicTag("th", decodeChunks(chunks)),
      td: (chunks) => formatBasicTag("td", decodeChunks(chunks)),
      br: <br />,
      hr: <hr />,
      wbr: <wbr />,
      // Tag with attributes
      a: (chunks) =>
        formatTagWithAttributes("a", decodeChunks(chunks), ["url"]),
      abbr: (chunks) =>
        formatTagWithAttributes("abbr", decodeChunks(chunks), ["title"]),
      // Example of custom tag / element
      highlight: (chunks) =>
        formatTagWithAttributes("highlight", decodeChunks(chunks), ["color"]),
      // additional / custom values
      // "This is a dynamic {name}" -- values: {"name": "Andrew"}
      // Other potential formatting options: https://formatjs.io/docs/icu-messageformat-parser
      ...props.values,
    }
  );

  // Add aria-label only to heading tags (h1-h6) with plain text
  // This ensures screen readers announce heading content correctly without DOM fragmentation issues
  const headingTags = ["h1", "h2", "h3", "h4", "h5", "h6"];

  // If it's a React element (e.g., <h4>...</h4>), clone it and add aria-label if it's a heading
  if (React.isValidElement(formattedMessage)) {
    // Only add aria-label to heading tags
    if (headingTags.includes(formattedMessage.type)) {
      const plainText = extractPlainText(formattedMessage);

      return React.cloneElement(formattedMessage, {
        ...formattedMessage.props,
        "aria-label": plainText,
      });
    }

    return formattedMessage;
  }

  // If it's an array (common when intl.formatMessage has multiple formatters)
  if (Array.isArray(formattedMessage)) {
    // If the first item is a heading element, add aria-label to it
    if (
      formattedMessage.length > 0 &&
      React.isValidElement(formattedMessage[0])
    ) {
      const firstElement = formattedMessage[0];

      // Only add aria-label to heading tags
      if (headingTags.includes(firstElement.type)) {
        // Extract plain text ONLY from the first element, not the entire array
        const plainText = extractPlainText(firstElement);

        const clonedFirstElement = React.cloneElement(firstElement, {
          ...firstElement.props,
          "aria-label": plainText,
        });

        // Return array with the cloned first element
        return [clonedFirstElement, ...formattedMessage.slice(1)];
      }
    }
  }

  // If it's a plain string or other primitive, just return it as-is
  // (no aria-label needed since there's no DOM fragmentation)
  return formattedMessage;
}
const ALLOWED_SANITIZE_CONFIGS = [
  sanitizeConfigOptions.PlainText,
  sanitizeConfigOptions.LightInlineText,
  sanitizeConfigOptions.InlineText,
  sanitizeConfigOptions.LightBlockText,
  sanitizeConfigOptions.BlockText,
  sanitizeConfigOptions.ButtonText,
  sanitizeConfigOptions.TableText,
  sanitizeConfigOptions.ListText,
  sanitizeConfigOptions.All,
];
function getAllowedTagsFromConfigs() {
  const allTags = [];
  ALLOWED_SANITIZE_CONFIGS.forEach((config) => {
    if (config && config.allowedTags) {
      allTags.push(...config.allowedTags);
    }
  });
  // Return a unique list of allowedTags
  return Array.from(new Set(allTags));
}
const ALLOWED_TAGS = getAllowedTagsFromConfigs();
FormattedMessage.propTypes = {
  string: PropTypes.string.isRequired,
  fallbackString: PropTypes.string,
  sanitizeConfig: PropTypes.oneOf(ALLOWED_SANITIZE_CONFIGS),
  allowAdditionalTags: PropTypes.arrayOf(PropTypes.oneOf(ALLOWED_TAGS)),
  excludeTags: PropTypes.arrayOf(PropTypes.oneOf(ALLOWED_TAGS)),
  values: PropTypes.object,
};
FormattedMessage.defaultProps = {
  sanitizeConfig: sanitizeConfigOptions.PlainText,
  allowAdditionalTags: [],
  excludeTags: [],
  values: {},
  fallbackString: null,
};
